import { consume, ContextType } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import ms from 'ms';
import { contexts, FKDataSnapshotRefresh } from '../contexts.js';

// A snapshot can be superseded seconds after the server builds it, and prompting
// someone to swap it for one barely newer is noise. Stay quiet until the snapshot
// is this old. The age counts from creation rather than from the page load: a tab
// opened on an existing snapshot (e.g. by a cmd-click) may be showing old data
// from its very first render.
// project-context re-polls the snapshot status every minute, so a render that
// suppressed the button is re-run on its own soon enough.
const QUIET_PERIOD = ms('1 minute');

/**
 * Sits in the project header and appears once the data snapshot the page was
 * rendered from has been superseded. Clicking it asks project-context to swap
 * in a fresh snapshot, which re-runs every task bound to the snapshot id.
 */
@customElement('snapshot-refresh-button')
class SnapshotRefreshButton extends LitElement {
  @consume({ context: contexts.dataSnapshotInfo, subscribe: true }) @state() private _dataSnapshotInfo?: ContextType<typeof contexts.dataSnapshotInfo>;

  private _isTooFresh() {
    const createdAt = this._dataSnapshotInfo?.createdAt;
    return createdAt !== undefined && Date.now() - createdAt < QUIET_PERIOD;
  }

  render() {
    const info = this._dataSnapshotInfo;
    const loading = info?.loadingStarted !== undefined &&
        (info.loadingFinished === undefined || info.loadingFinished < info.loadingStarted);
    if (loading && info?.snapshotId) {
      return html`
        <sl-button disabled>
          <sl-spinner slot=prefix></sl-spinner>
          Loading New Data
        </sl-button>
      `;
    }
    if (info?.status !== 'outdated' || info.createdAt === undefined)
      return nothing;
    if (this._isTooFresh())
      return nothing;
    return html`
      <sl-tooltip hoist placement=bottom>
        <div slot=content>
          <!-- sync keeps the phrase current on its own. The date binding never changes,
               so without it a re-render of this component leaves the stale text in place. -->
          <p>Showing data from <sl-relative-time sync date=${new Date(info.createdAt).toISOString()}></sl-relative-time>.</p>
          <p>Click to load latest data.</p>
        </div>
        <sl-button @click=${() => this.dispatchEvent(new FKDataSnapshotRefresh())}>
          <span slot=prefix class=dot></span>
          New Data Available
        </sl-button>
      </sl-tooltip>
    `;
  }

  static styles = [css`
    /* Contribute no box of our own, so the header lays out identically when we render nothing. */
    :host {
      display: contents;
    }

    sl-button {
      --ping-opacity: 0.3;

      &:hover {
        --ping-opacity: 0.55;

        .dot {
          background-color: var(--sl-color-primary-600);
        }
      }
    }

    sl-spinner {
      font-size: var(--sl-font-size-small);
    }

    /*
     * Stripped the same way as timezone-selector[subtle], which sits right next to us:
     * both controls then share a font, size, weight and baseline because they are the
     * same component, rather than because two stylesheets agree.
     */
    sl-button::part(base) {
      background: none;
      border-color: transparent;
      border-radius: var(--sl-border-radius-medium);
      border: none;
      color: var(--sl-color-neutral-400);
    }

    sl-button:hover::part(base) {
      background-color: var(--sl-color-neutral-100);
      color: var(--sl-color-neutral-900);
    }

    .dot {
      position: relative;
      flex: none;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--sl-color-primary-400);
      transition: background-color var(--sl-transition-fast);

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background-color: inherit;
        animation: snapshot-refresh-ping 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
      }
    }

    @keyframes snapshot-refresh-ping {
      0% { transform: scale(1); opacity: var(--ping-opacity); }
      70%, 100% { transform: scale(2.4); opacity: 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .dot::after {
        display: none;
      }
    }
  `];
}
